// Auto-extracted tab: risks
import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * RisksTab - extracted from app.jsx
 * Original lines: 8948-9225
 * 
 * TODO: This component needs props passed from AppContent:
 * - darkMode, scenarios, thresholds, etc.
 * - All the state and handlers it uses
 */
export function RisksTab(props) {
  // Original render content (needs refactoring to use props):
  return (
    <>
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`p-4 rounded-xl ${darkMode ? "bg-slate-800/50 border border-slate-700" : "bg-white border border-slate-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>{risks.length}</div>
                <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Total Risks</div>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? "bg-red-900/30 border border-red-800" : "bg-red-50 border border-red-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-red-400" : "text-red-700"}`}>
                  {risks.filter(r => (r.residual_risk_score || 0) >= 15).length}
                </div>
                <div className={`text-sm ${darkMode ? "text-red-300" : "text-red-600"}`}>High/Critical (≥15)</div>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? "bg-blue-900/30 border border-blue-800" : "bg-blue-50 border border-blue-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-blue-400" : "text-blue-700"}`}>
                  ${risks.reduce((sum, r) => sum + (r.annual_loss_expectancy || 0), 0).toLocaleString()}
                </div>
                <div className={`text-sm ${darkMode ? "text-blue-300" : "text-blue-600"}`}>Total ALE</div>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? "bg-emerald-900/30 border border-emerald-800" : "bg-emerald-50 border border-emerald-200"}`}>
                <div className={`text-2xl font-bold ${darkMode ? "text-emerald-400" : "text-emerald-700"}`}>
                  {risks.filter(r => r.risk_treatment === 'mitigate').length}
                </div>
                <div className={`text-sm ${darkMode ? "text-emerald-300" : "text-emerald-600"}`}>Being Mitigated</div>
              </div>
            </div>
            
            {/* Risk Heat Map - Modern 2026 Design */}
            <Card darkMode={darkMode} title="Risk Heat Map" subtitle="5×5 Likelihood vs Impact Matrix">
              {/* Toggle Buttons */}
              <div className="flex gap-2 mb-6">
                <button 
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    riskHeatMapType === 'inherent' 
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/25' 
                      : darkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  onClick={() => setRiskHeatMapType('inherent')}
                >
                  Inherent Risk
                </button>
                <button 
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    riskHeatMapType === 'residual' 
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/25' 
                      : darkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  onClick={() => setRiskHeatMapType('residual')}
                >
                  Residual Risk
                </button>
              </div>
              
              {/* Modern Heat Map Grid */}
              <div className="flex gap-4">
                {/* Y-Axis Labels */}
                <div className="flex flex-col justify-between py-1">
                  {[
                    { level: 5, label: "Certain", short: "5" },
                    { level: 4, label: "Likely", short: "4" },
                    { level: 3, label: "Possible", short: "3" },
                    { level: 2, label: "Unlikely", short: "2" },
                    { level: 1, label: "Rare", short: "1" }
                  ].map(({ level, label, short }) => (
                    <div key={level} className="flex items-center gap-2 h-14">
                      <span className={`text-xs font-medium w-20 text-right ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                        {label}
                      </span>
                      <span className={`text-[10px] font-bold w-4 ${darkMode ? "text-slate-600" : "text-slate-300"}`}>
                        {short}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Grid */}
                <div className="flex-1">
                  <div className="grid grid-cols-5 gap-2">
                    {[5,4,3,2,1].map(likelihood => (
                      [1,2,3,4,5].map(impact => {
                        const score = likelihood * impact;
                        const cellRisks = risks.filter(r => {
                          const l = riskHeatMapType === 'inherent' ? r.inherent_likelihood : r.residual_likelihood;
                          const i = riskHeatMapType === 'inherent' ? r.inherent_impact : r.residual_impact;
                          return l === likelihood && i === impact;
                        });
                        
                        // Vivid gradient colors
                        const cellStyle = score >= 20 
                          ? "bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/40 hover:shadow-red-500/60" 
                          : score >= 15 
                            ? "bg-gradient-to-br from-orange-500 to-red-500 shadow-orange-500/40 hover:shadow-orange-500/60"
                            : score >= 10 
                              ? "bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/40 hover:shadow-amber-500/60"
                              : score >= 5 
                                ? "bg-gradient-to-br from-yellow-400 to-amber-400 shadow-yellow-500/40 hover:shadow-yellow-500/60"
                                : "bg-gradient-to-br from-emerald-400 to-green-500 shadow-green-500/40 hover:shadow-green-500/60";
                        
                        const impactLabels = ["Negligible", "Minor", "Moderate", "Major", "Severe"];
                        const likelihoodLabels = ["Rare", "Unlikely", "Possible", "Likely", "Certain"];
                        
                        return (
                          <div 
                            key={`${likelihood}-${impact}`}
                            className={`h-14 ${cellStyle} rounded-xl shadow-lg flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl hover:z-10 group`}
                            title={`${likelihoodLabels[likelihood-1]} × ${impactLabels[impact-1]} = Score ${score}`}
                            onClick={() => {
                              if (cellRisks.length > 0) {
                                setRiskDetailModal({ open: true, risk: cellRisks[0] });
                              }
                            }}
                          >
                            {cellRisks.length > 0 ? (
                              <div className="flex flex-col items-center">
                                <span className="text-white text-lg font-bold drop-shadow-sm">{cellRisks.length}</span>
                                <span className="text-white/70 text-[9px] font-medium">risk{cellRisks.length > 1 ? 's' : ''}</span>
                              </div>
                            ) : (
                              <span className="text-white/30 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">{score}</span>
                            )}
                          </div>
                        );
                      })
                    ))}
                  </div>
                  
                  {/* X-Axis Labels */}
                  <div className="flex justify-between mt-3 px-1">
                    {[
                      { label: "Negligible", short: "1" },
                      { label: "Minor", short: "2" },
                      { label: "Moderate", short: "3" },
                      { label: "Major", short: "4" },
                      { label: "Severe", short: "5" }
                    ].map(({ label, short }, i) => (
                      <div key={i} className="flex flex-col items-center">
                        <span className={`text-[10px] font-bold ${darkMode ? "text-slate-600" : "text-slate-300"}`}>{short}</span>
                        <span className={`text-xs font-medium ${darkMode ? "text-slate-500" : "text-slate-500"}`}>{label}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Axis Label */}
                  <div className={`text-center mt-2 text-[11px] font-semibold uppercase tracking-wider ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                    Impact →
                  </div>
                </div>
              </div>
              
              {/* Legend */}
              <div className={`flex items-center justify-center gap-6 mt-6 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-md bg-gradient-to-br from-emerald-400 to-green-500 shadow-sm"></span>
                  <span className={`text-xs font-medium ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Low (1-4)</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-md bg-gradient-to-br from-yellow-400 to-amber-400 shadow-sm"></span>
                  <span className={`text-xs font-medium ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Medium (5-9)</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-md bg-gradient-to-br from-amber-400 to-orange-500 shadow-sm"></span>
                  <span className={`text-xs font-medium ${darkMode ? "text-slate-500" : "text-slate-500"}`}>High (10-14)</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-md bg-gradient-to-br from-orange-500 to-red-500 shadow-sm"></span>
                  <span className={`text-xs font-medium ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Very High (15-19)</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-md bg-gradient-to-br from-red-500 to-red-600 shadow-sm"></span>
                  <span className={`text-xs font-medium ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Critical (20-25)</span>
                </span>
              </div>
            </Card>
            
            {/* Risk Register Table */}
            <Card 
              darkMode={darkMode} 
              title="Risk Register" 
              subtitle="All identified risks with quantification"
              actions={
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={loadRiskData} disabled={riskLoading}>
                    <Icons.Refresh className={riskLoading ? "animate-spin" : ""} />
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setRiskModal({ open: true, risk: null })}>
                    <Icons.Plus /> Add Risk
                  </Button>
                </div>
              }
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className={darkMode ? "bg-slate-800/50" : "bg-gradient-to-r from-slate-50 to-slate-100/80"}>
                    <tr>
                      <th className={`text-left py-3.5 px-4 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Risk ID</th>
                      <th className={`text-left py-3.5 px-4 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Name</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Category</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Inherent</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Residual</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>ALE</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Treatment</th>
                      <th className={`text-left py-3 px-2 font-medium ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {risks.map(risk => (
                      <tr key={risk.id} className={`${darkMode ? "border-slate-700/50 hover:bg-slate-800/50" : "border-slate-100 hover:bg-slate-50"} border-b`}>
                        <td className={`py-3 px-2 font-mono ${darkMode ? "text-blue-400" : "text-blue-600"}`}>{risk.risk_id}</td>
                        <td className={`py-3 px-2 font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{risk.name}</td>
                        <td className={`py-3 px-2 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{risk.risk_category}</td>
                        <td className="py-3 px-2">
                          <Badge variant={risk.inherent_risk_score >= 15 ? 'critical' : risk.inherent_risk_score >= 10 ? 'high' : risk.inherent_risk_score >= 5 ? 'medium' : 'low'}>
                            {risk.inherent_risk_score || '-'}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant={risk.residual_risk_score >= 15 ? 'critical' : risk.residual_risk_score >= 10 ? 'high' : risk.residual_risk_score >= 5 ? 'medium' : 'low'}>
                            {risk.residual_risk_score || '-'}
                          </Badge>
                        </td>
                        <td className={`py-3 px-2 font-mono ${darkMode ? "text-emerald-400" : "text-emerald-600"}`}>
                          {risk.annual_loss_expectancy ? `$${risk.annual_loss_expectancy.toLocaleString()}` : '-'}
                        </td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            risk.risk_treatment === 'mitigate' ? (darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-700') :
                            risk.risk_treatment === 'accept' ? (darkMode ? 'bg-amber-900/50 text-amber-400' : 'bg-amber-100 text-amber-700') :
                            risk.risk_treatment === 'transfer' ? (darkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-700') :
                            (darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600')
                          }`}>
                            {risk.risk_treatment || 'Not Set'}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="xs" onClick={() => setRiskDetailModal({ open: true, risk })}>
                              <Icons.Eye />
                            </Button>
                            <Button variant="ghost" size="xs" onClick={() => openRiskEdit(risk)}>
                              <Icons.Edit />
                            </Button>
                            <Button variant="ghost" size="xs" onClick={() => setRiskQuantModal({ open: true, risk })}>
                              <Icons.Calculator /> Calculate
                            </Button>
                            <Button variant="ghost" size="xs" onClick={() => openDeleteModal("risk", risk)}>
                              <Icons.Trash className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {risks.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-4">
                          <EducationalEmptyState
                            title="No risks in register"
                            examinerSays="Walk me through your risk assessment. What are your top BSA/AML risks? How do you quantify them? What mitigating controls do you have?"
                            example="High-Risk Customer Concentration - Inherent Risk: High. Mitigating Controls: Enhanced CDD, quarterly reviews, transaction monitoring. Residual Risk: Medium. Annual loss expectancy: $50,000."
                            templates={[
                              { name: "High-Risk Customer Concentration", type: "hrcc" },
                              { name: "Wire Transfer Fraud", type: "wire_fraud" },
                              { name: "Third-Party Risk", type: "tprm" },
                              { name: "Regulatory Non-Compliance", type: "reg_compliance" },
                            ]}
                            onAddBlank={() => setRiskModal({ open: true, risk: null })}
                            onSelectTemplate={(t) => setRiskModal({ open: true, risk: null, template: t.type })}
                            darkMode={darkMode}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
    </>
  );
}

export default RisksTab;
